import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Divider,
  Stack,
  Link,
  Checkbox,
  FormControlLabel,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useTranslation, Trans } from 'react-i18next';
import { register } from '../lib/auth';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { toast } from '../lib/toast';
import LanguageSwitcher from '../components/LanguageSwitcher';
import HCaptcha, { HCaptchaRef } from '../components/HCaptcha';
import GoogleSignInButton from '../components/GoogleSignInButton';
import { useHCaptchaSiteKey } from '../hooks/useCommon';
import { translateBackendError } from '../utils/errorTranslator';
import { useLanguage } from '../hooks/useLanguage';
import { detectLanguageFromMarketing } from '../utils/languageDetection';
import { useAuth } from '../hooks/useAuth';
import { LEGAL_URLS } from '../lib/legalUrls';

const StyledCard = styled(Card)(() => ({
  maxWidth: '420px',
  width: '95%',
  '@media (min-width: 600px)': {
    width: '90%',
  },
  '@media (min-width: 900px)': {
    width: '420px',
  },
  margin: '0 auto',
  boxShadow: '0 10px 25px rgba(0, 0, 0, 0.1)',
  overflow: 'hidden',
  borderRadius: 16,
}));

function RegistrationPage() {
  const { t, i18n } = useTranslation('auth');
  const { changeLanguage, currentLanguage } = useLanguage();
  const { googleLogin } = useAuth();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    first_name: '',
    last_name: '',
    website: '' // Honeypot field - should remain empty for real users
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [hcaptchaToken, setHcaptchaToken] = useState<string | null>(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const hcaptchaRef = useRef<HCaptchaRef | null>(null);

  const navigate = useNavigate();
  const { data: hcaptchaConfig, isLoading: hcaptchaLoading } = useHCaptchaSiteKey();

  // Detect and set language from marketing site on mount
  // Only apply marketing language if user has NO saved language preference yet
  useEffect(() => {
    const hasExistingPreference = localStorage.getItem('preferred_language');
    if (!hasExistingPreference) {
      const detectedLang = detectLanguageFromMarketing();
      if (detectedLang) {
        changeLanguage(detectedLang).catch(() => {
          // Silently fail if language change fails
        });
      }
    }
  }, [changeLanguage]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };


  const validateForm = () => {
    if (!formData.email || !formData.password || !formData.first_name) {
      setError(t('register.errors.requiredFields'));
      return false;
    }

    if (formData.password !== formData.confirmPassword) {
      setError(t('register.errors.passwordMismatch'));
      return false;
    }

    if (formData.password.length < 8) {
      setError(t('register.errors.weakPassword'));
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError(t('register.errors.invalidEmail'));
      return false;
    }

    // Check hCaptcha if enabled
    if (hcaptchaConfig?.enabled && !hcaptchaToken) {
      setError('Please complete the security verification');
      return false;
    }

    if (!termsAccepted) {
      setError(t('terms.errorRequired'));
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!validateForm()) {
      return;
    }

    setLoading(true);

    try {
      // Get current language from i18n (fallback to 'en' if not set)
      // Extract language code (e.g., "fr-FR" -> "fr", "en" -> "en")
      const langCode = i18n.language?.substring(0, 2) || currentLanguage || 'en';
      // Validate language code (only en, fr, es are supported)
      const validLanguages = ['en', 'fr', 'es'];
      const userLanguage = validLanguages.includes(langCode) ? langCode : 'en';

      const result = await register({
        email: formData.email,
        password: formData.password,
        first_name: formData.first_name,
        last_name: formData.last_name,
        role: 'client', // All new users are automatically 'client' - only admins can assign admin role
        preferred_language: userLanguage,
        accept_terms: true,
        hcaptcha_token: hcaptchaToken || undefined,
        honeypot_field: formData.website || undefined // Send honeypot field value (should be empty for real users)
      });

      if (result.success) {
        setRegistrationSuccess(true);
      } else {
        const translatedError = translateBackendError(result.error || '');
        setError(translatedError);
        toast.raw.error(translatedError);
        // Reset hCaptcha on error
        if (hcaptchaRef.current) {
          hcaptchaRef.current.reset();
        }
        setHcaptchaToken(null);
      }
    } catch (err: any) {
      const errorMessage = err?.response?.data?.detail || err?.message || t('register.errors.unexpected');
      const translatedError = translateBackendError(errorMessage);
      setError(translatedError);
      toast.raw.error(translatedError);
      // Reset hCaptcha on error
      if (hcaptchaRef.current) {
        hcaptchaRef.current.reset();
      }
      setHcaptchaToken(null);
    } finally {
      setLoading(false);
    }
  };

  const handleHCaptchaVerify = useCallback((token: string) => {
    setHcaptchaToken(token);
    setError(''); // Clear any previous errors
  }, []);

  const handleHCaptchaError = useCallback((error: string) => {
    setError('Security verification failed. Please try again.');
    setHcaptchaToken(null);
  }, []);

  const handleHCaptchaExpire = useCallback(() => {
    setHcaptchaToken(null);
  }, []);

  // Google Sign-In handlers
  const handleGoogleSuccess = useCallback(async (credential: string) => {
    setError('');
    setLoading(true);
    try {
      const currentLang = currentLanguage || i18n.language?.substring(0, 2) || null;
      const result = await googleLogin(credential, currentLang, true);
      if (result.success) {
        navigate('/dashboard');
      } else {
        const translatedError = translateBackendError(result.error || '');
        setError(translatedError);
        toast.raw.error(translatedError);
      }
    } catch (err) {
      const errorMessage = t('google.error', 'Google sign-in failed. Please try again.');
      setError(errorMessage);
      toast.raw.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [googleLogin, navigate, t, currentLanguage, i18n.language]);

  const handleGoogleError = useCallback((_error: string) => {
    const errorMessage = t('google.error', 'Google sign-in failed. Please try again.');
    setError(errorMessage);
    toast.raw.error(errorMessage);
  }, [t]);

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '3fr 2fr' }, minHeight: '100vh' }}>

      {/* ── Left: image panel ── */}
      <Box
        sx={{
          position: 'relative',
          backgroundImage: 'url(/login-bg.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          display: { xs: 'none', md: 'flex' },
          alignItems: 'flex-end',
          padding: '3rem',
          '&::before': {
            content: '""',
            position: 'absolute',
            inset: 0,
            background: 'linear-gradient(to bottom, rgba(0,0,0,0.15), rgba(0,0,0,0.55))',
          }
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 1, color: 'white' }}>
          <Box sx={{
            fontSize: 'clamp(1.8rem, 3vw, 2.8rem)',
            fontWeight: 700,
            letterSpacing: '-0.5px',
            textShadow: '0 2px 12px rgba(0,0,0,0.4)',
            mb: 1,
          }}>
            BizContent.ai
          </Box>
          <Box sx={{
            fontSize: 'clamp(1rem, 1.5vw, 1.25rem)',
            fontWeight: 400,
            opacity: 0.9,
            textShadow: '0 1px 8px rgba(0,0,0,0.4)',
            maxWidth: '600px',
            lineHeight: 1.5,
          }}>
            {t('register.subtitle')}
          </Box>
        </Box>
      </Box>

      {/* ── Right: form panel ── */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #f5f3ff 0%, #faf8ff 60%, #f0f4ff 100%)',
        overflowY: 'auto',
        py: 4,
        px: 2,
      }}>
      <StyledCard>
        {/* Form Section */}
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h5" component="h2" gutterBottom align="center" sx={{ mb: 2, color: 'text.primary' }}>
            {t('register.createAccount')}
          </Typography>

          {registrationSuccess ? (
            <Stack spacing={2} sx={{ mt: 2 }}>
              <Alert severity="info" sx={{ textAlign: 'left' }}>
                <strong>{t('register.checkEmailTitle', 'Almost there!')}</strong>
                <br />
                {t('register.checkEmailBody', 'We sent a verification link to your email address. Please check your inbox and click the link to activate your account before logging in.')}
              </Alert>
              <Box sx={{ textAlign: 'center', mt: 1 }}>
                <Button
                  component={RouterLink}
                  to="/login"
                  variant="contained"
                  size="medium"
                  sx={{
                    width: '100%',
                    maxWidth: '400px',
                    py: 1,
                    fontSize: '1rem',
                    fontWeight: 600,
                    background: 'linear-gradient(135deg, #8b9cff 0%, #ff6b9d 100%)',
                    boxShadow: '0 2px 10px rgba(139, 156, 255, 0.3)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #7a8bff 0%, #ff5a8d 100%)',
                      boxShadow: '0 4px 15px rgba(139, 156, 255, 0.4)',
                    }
                  }}
                >
                  {t('register.goToLogin', 'Go to Login')}
                </Button>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                <LanguageSwitcher variant="compact" showLabel={false} />
              </Box>
            </Stack>
          ) : (
          <>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            <Stack spacing={1.5}>
              <Stack direction="row" spacing={1.5}>
                <TextField
                  fullWidth
                  label={t('register.firstName')}
                  name="first_name"
                  value={formData.first_name}
                  onChange={handleChange}
                  required
                  size="small"
                  placeholder={t('register.firstNamePlaceholder')}
                />
                <TextField
                  fullWidth
                  label={t('register.lastName')}
                  name="last_name"
                  value={formData.last_name}
                  onChange={handleChange}
                  size="small"
                  placeholder={t('register.lastNamePlaceholder')}
                />
              </Stack>

              <TextField
                fullWidth
                label={t('register.email')}
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
                size="small"
                placeholder={t('register.emailPlaceholder')}
              />

              <TextField
                fullWidth
                label={t('register.password')}
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                required
                size="small"
                placeholder={t('register.passwordPlaceholder')}
                helperText={t('register.passwordHelp')}
              />

              <TextField
                fullWidth
                label={t('register.confirmPassword')}
                name="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
                size="small"
                placeholder={t('register.confirmPasswordPlaceholder')}
              />

              {/* Honeypot field - hidden from real users, bots will fill it */}
              <TextField
                name="website"
                value={formData.website}
                onChange={handleChange}
                autoComplete="off"
                tabIndex={-1}
                aria-hidden="true"
                sx={{
                  position: 'absolute',
                  left: '-9999px',
                  opacity: 0,
                  height: 0,
                  width: 0,
                  overflow: 'hidden',
                }}
              />
            </Stack>

            {/* Terms and policies acceptance */}
            <FormControlLabel
              control={
                <Checkbox
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  color="primary"
                  size="small"
                />
              }
              label={
                <Typography component="span" variant="body2" sx={{ fontSize: '0.75rem' }}>
                  <Trans
                    i18nKey="auth:terms.acceptLabel"
                    components={[
                      <Link href={LEGAL_URLS.terms} target="_blank" rel="noopener noreferrer" key="terms" sx={{ fontSize: 'inherit' }} />,
                      <Link href={LEGAL_URLS.privacy} target="_blank" rel="noopener noreferrer" key="privacy" sx={{ fontSize: 'inherit' }} />,
                      <Link href={LEGAL_URLS.cookies} target="_blank" rel="noopener noreferrer" key="cookies" sx={{ fontSize: 'inherit' }} />,
                    ]}
                  />
                </Typography>
              }
              sx={{ alignItems: 'center', mt: 3 }}
            />

            {/* hCaptcha Widget */}
            {!hcaptchaLoading && (
              <>
                {hcaptchaConfig?.enabled && hcaptchaConfig?.site_key ? (
                  <Box sx={{ mt: 1, mb: 1, display: 'flex', justifyContent: 'center' }}>
                    <HCaptcha
                      ref={hcaptchaRef}
                      siteKey={hcaptchaConfig.site_key}
                      onVerify={handleHCaptchaVerify}
                      onError={handleHCaptchaError}
                      onExpire={handleHCaptchaExpire}
                      theme="light"
                      size="normal"
                    />
                  </Box>
                ) : import.meta.env.DEV && (
                  <Box sx={{ mt: 2, mb: 2, p: 2, bgcolor: 'info.light', borderRadius: 1, textAlign: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                      hCaptcha is not configured. Add HCAPTCHA_SITE_KEY and HCAPTCHA_SECRET_KEY to backend .env
                    </Typography>
                  </Box>
                )}
              </>
            )}
            {hcaptchaLoading && (
              <Box sx={{ mt: 2, mb: 2, display: 'flex', justifyContent: 'center' }}>
                <CircularProgress size={24} />
              </Box>
            )}

            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2, mb: 1 }}>
              <Button
                type="submit"
                variant="contained"
                size="medium"
                disabled={loading || !termsAccepted}
                sx={{
                  width: '100%',
                  maxWidth: '400px',
                  py: 1,
                  fontSize: '1rem',
                  fontWeight: 600,
                  background: 'linear-gradient(135deg, #8b9cff 0%, #ff6b9d 100%)',
                  boxShadow: '0 2px 10px rgba(139, 156, 255, 0.3)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #7a8bff 0%, #ff5a8d 100%)',
                    boxShadow: '0 4px 15px rgba(139, 156, 255, 0.4)',
                  }
                }}
              >
                {loading ? <CircularProgress size={20} color="inherit" /> : t('register.submit')}
              </Button>
            </Box>
          </Box>

          <Divider sx={{ my: 3 }}>
            <Typography variant="body2" color="text.secondary">
              {t('google.or', 'or')}
            </Typography>
          </Divider>

          <Box sx={{ mb: 3 }}>
            <GoogleSignInButton
              onSuccess={handleGoogleSuccess}
              onError={handleGoogleError}
              buttonText="signup_with"
              disabled={loading}
            />
            <Typography variant="body2" sx={{ fontSize: '0.75rem', color: 'text.secondary', mt: 1.5, textAlign: 'center' }}>
              <Trans
                i18nKey="auth:terms.byContinuingWithGoogle"
                components={[
                  <Link href={LEGAL_URLS.terms} target="_blank" rel="noopener noreferrer" key="terms" sx={{ fontSize: 'inherit' }} />,
                  <Link href={LEGAL_URLS.privacy} target="_blank" rel="noopener noreferrer" key="privacy" sx={{ fontSize: 'inherit' }} />,
                  <Link href={LEGAL_URLS.cookies} target="_blank" rel="noopener noreferrer" key="cookies" sx={{ fontSize: 'inherit' }} />,
                ]}
              />
            </Typography>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ textAlign: 'center', mb: 1 }}>
            <Typography variant="body2" color="text.secondary">
              {t('register.hasAccount')}{' '}
              <Link component={RouterLink} to="/login" variant="body2">
                {t('register.signIn')}
              </Link>
            </Typography>
          </Box>

          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
            <LanguageSwitcher variant="compact" showLabel={false} />
          </Box>
          </>
          )}
        </CardContent>
      </StyledCard>
      </Box>
    </Box>
  );
}

export default RegistrationPage;
